import * as React from "react";
import { GraphQLTypes } from "kw/api/zeus";
import { useUpdatePolicy } from "../hooks/useUpdatePolicy";
import { useAvailableCovers } from "../hooks/usePolicyCovers";
import { get, partition, set, isEmpty } from "lodash";
import { toCamelCase } from "kanopi-components/utils/string";

type TProps = {
  policy: TPolicy;
  children: React.ReactChildren;
};

type TPolicyContext = {
  riskData: TRiskData;
  setRiskData: (TRiskData) => void;
  activeCovers: TCoverSection[];
  inactiveCovers: TCoverSection[];
  handleUpdatePolicyCover: (
    enabled: boolean,
    cover: GraphQLTypes["CoverType"],
    answers: GraphQLTypes["AnswerType"][]
  ) => void;
  loading: boolean;
};

type TRiskDataState = {
  covers?: TCoverSection[];
};

const PolicyProviderContext = React.createContext<TPolicyContext | null>(null);

function PolicyProvider({ children, policy }: TProps) {
  const [riskData, setRiskData] = React.useState<TRiskDataState>({});
  const [activeCovers, setActiveCovers] = React.useState<TCoverSection[]>(
    policy?.quote?.covers
  );
  const [inactiveCovers, setInactiveCovers] = React.useState<TCoverSection[]>([]);
  const [updateDraft, setUpdateDraft] = React.useState<boolean>(false);

  const { updatePolicy, loading: submitting } = useUpdatePolicy({
    policyId: policy?.id,
    riskData,
  });

  const { availableCovers, loading: loadingCovers } = useAvailableCovers({
    riskProfileId: policy?.riskProfile?.id,
  });

  React.useEffect(() => {
    if (!isEmpty(policy?.draftUpdate?.riskProfile?.riskData)) {
      setRiskData(policy?.draftUpdate.riskProfile.riskData);
    } else {
      setRiskData(policy?.riskProfile?.riskData);
    }
  }, [policy]);

  React.useEffect(() => {
    if (!loadingCovers && riskData?.covers) {
      const [activeCovers, inactiveCovers] = partition(availableCovers, (c) =>
        get(riskData, `covers.${toCamelCase(c.key)}.enabled`, false)
      );
      setActiveCovers(activeCovers);
      setInactiveCovers(inactiveCovers);
    }
  }, [loadingCovers, riskData?.covers]);

  React.useEffect(() => {
    if (!isEmpty(riskData) && updateDraft) {
      updatePolicy();
    }
  }, [riskData, updateDraft]);

  function getUpdatedCoverData({ cover, enabled, answers }) {
    const updatedRiskData = { ...riskData };
    set(updatedRiskData, `covers.${toCamelCase(cover.key)}.enabled`, enabled);
    for (const answerKey in answers) {
      set(updatedRiskData, answerKey, answers[answerKey]);
    }
    return updatedRiskData;
  }

  const handleUpdatePolicyCover = ({ enabled = false, cover, answers = {} }) => {
    const newRiskData = getUpdatedCoverData({ cover, answers, enabled });
    setRiskData({ ...riskData, ...newRiskData });
    setUpdateDraft(true);
  };

  const value = {
    riskData,
    setRiskData,
    activeCovers,
    inactiveCovers,
    handleUpdatePolicyCover,
    loading: loadingCovers || submitting || false,
  };

  return (
    <PolicyProviderContext.Provider value={value}>
      {children}
    </PolicyProviderContext.Provider>
  );
}

const usePolicyContext = () => React.useContext(PolicyProviderContext);

export { PolicyProvider, usePolicyContext, PolicyProviderContext };
